import { useEffect, useRef, useState } from "react";
import { trackBlogEngagement, type BlogEngagementKind } from "@/lib/analytics";

/**
 * Thin fixed reading progress bar at the very top of the viewport.
 * Tracks scroll position relative to the <article> element if provided,
 * otherwise the full document. Fires GA4 + DB engagement events at
 * 25/50/75/100% milestones, deduplicated per post per session.
 */
interface ReadingProgressProps {
  targetSelector?: string;
  postId?: string;
  postSlug?: string;
  postTitle?: string;
  language?: string;
}

const MILESTONES: Array<{ pct: number; kind: BlogEngagementKind }> = [
  { pct: 25, kind: "scroll_25" },
  { pct: 50, kind: "scroll_50" },
  { pct: 75, kind: "scroll_75" },
  { pct: 100, kind: "scroll_100" },
];

const sessionKey = (postId: string, kind: BlogEngagementKind) =>
  `blog-engagement:${postId}:${kind}`;

const ReadingProgress = ({
  targetSelector = "article",
  postId,
  postSlug,
  postTitle,
  language,
}: ReadingProgressProps) => {
  const [progress, setProgress] = useState(0);
  const firedRef = useRef<Set<BlogEngagementKind>>(new Set());

  useEffect(() => {
    const compute = () => {
      const el = targetSelector ? document.querySelector(targetSelector) : null;
      const scrollTop = window.scrollY;
      let pct = 0;

      if (el instanceof HTMLElement) {
        const start = el.offsetTop - 80;
        const total = el.offsetHeight - window.innerHeight + 80;
        if (total <= 0) {
          pct = scrollTop > start ? 100 : 0;
        } else {
          pct = ((scrollTop - start) / total) * 100;
        }
      } else {
        const docHeight =
          document.documentElement.scrollHeight - window.innerHeight;
        pct = docHeight <= 0 ? 0 : (scrollTop / docHeight) * 100;
      }

      pct = Math.max(0, Math.min(100, pct));
      setProgress(pct);

      // Milestone tracking - dedup per session per post
      if (postId) {
        for (const m of MILESTONES) {
          if (pct >= m.pct && !firedRef.current.has(m.kind)) {
            firedRef.current.add(m.kind);
            try {
              if (sessionStorage.getItem(sessionKey(postId, m.kind)) === "1") continue;
              sessionStorage.setItem(sessionKey(postId, m.kind), "1");
            } catch {
              // ignore storage errors, still fire once per page load
            }
            trackBlogEngagement(m.kind, {
              postId,
              slug: postSlug,
              title: postTitle,
              language,
              extra: { percent: m.pct },
            });
          }
        }
      }
    };

    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [targetSelector, postId, postSlug, postTitle, language]);

  // Reset milestone dedup when navigating to a different post
  useEffect(() => {
    firedRef.current = new Set();
  }, [postId]);

  return (
    <div
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 z-[60] h-1 bg-transparent pointer-events-none"
    >
      <div
        className="h-full bg-gradient-to-r from-primary via-accent to-primary transition-[width] duration-150 ease-out shadow-[0_0_12px_hsl(var(--primary)/0.6)]"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

export default ReadingProgress;
